import { type AxiosResponse } from 'axios'
import { type AxiosOptions } from 'shared/api'
import { baseApi } from 'entities/Auth'
import {
  type IParamsUnassignNumberFromInbox,
  type IParamsAddNumber,
  type IParamsAssignInbox,
  type IParamsCheckNumberOwnership,
  type IParamsCreateBulkInboxPerNumber,
  type IParamsCreateNumbersLandline,
  type IParamsCreateNumbersLandlineDocument,
  type IParamsCreateNumbersLandlineVerifyRequest,
  type IParamsCreateNumbersUnassigned,
  type IParamsDeleteNumbersLandLineDelete,
  type IParamsGetNumbersLandline,
  type IParamsGetNumbersLookup,
  type IParamsNumbers,
  type IParamsNumberSearch,
  type IParamsNumbersLandlineInboxUpdate,
  type IParamsNumbersLandlineTeamCreate,
  type IParamsNumbersPool,
  type IParamsReleaseNumber,
  type IParamsUpdateNumberPtf,
  type IResponseCreateBulkInboxPerNumber,
  type IResponseCreateNumbersLandline,
  type IResponseCreateNumbersLandlineDocument,
  type IResponseCreateNumbersLandlineVerifyRequest,
  type IResponseGetNumbersLandline,
  type IResponseGetNumbersLookup,
  type IResponseImportNumbersStatus,
  type IResponseIncomingTwilioNumbers,
  type IResponseIntegrationNumbers,
  type IResponseNumber,
  type IResponseNumbers,
  type IResponseNumberSearch,
  type IResponseNumberShortcodes,
  type IResponseNumbersLandlineInboxUpdate,
  type IResponseNumbersLandlineTeamCreate,
  type IResponseNumbersPool,
  type IResponseNumberVendorIntegrations,
  type IResponsePtfNumber,
  type IResponseReleaseNumber,
  type IParamsUpdateCallForwarding,
  type IResponseCallForwarding,
  type IResponseGetTeamByNumberId,
  type IResponseCheckNumberOwnership,
} from 'entities/Phone/api/types'

class Api {
  getOrganizationNumber(): Promise<AxiosResponse<IResponseNumber>> {
    return baseApi.get('organization/number')
  }

  getNumberVendorIntegrations(): Promise<AxiosResponse<IResponseNumberVendorIntegrations>> {
    return baseApi.get('numbers/vendors/integration')
  }

  getNumbersShortcodes(): Promise<AxiosResponse<IResponseNumberShortcodes[]>> {
    return baseApi.get('numbers/shortcodes')
  }

  getTeamByNumberId(numberId: number): Promise<AxiosResponse<IResponseGetTeamByNumberId>> {
    return baseApi.get(`numbers/${numberId}/team`)
  }

  getNumbersSearch(
    params: IParamsNumberSearch,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseNumberSearch[]>> {
    return baseApi.post('numbers/search', params, options)
  }

  getAircallNumbers(
    params: IParamsNumbers,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseIntegrationNumbers>> {
    return baseApi.get('aircall/numbers', { params, ...options })
  }

  getNumbers(
    params: IParamsNumbers,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseNumbers>> {
    return baseApi.get('numbers', { params, ...options })
  }

  refreshAircallNumbers(): Promise<AxiosResponse<IResponseIntegrationNumbers>> {
    return baseApi.get('aircall/numbers-refresh')
  }

  getUnassignedNumbers(
    params: IParamsNumbers,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseNumbers>> {
    return baseApi.get('numbers/unassigned', { params, ...options })
  }

  getNumbersFromPool(params: IParamsNumbersPool): Promise<AxiosResponse<IResponseNumbersPool>> {
    return baseApi.get('numbers/get-from-pool', { params })
  }

  addNumber(params: IParamsAddNumber): Promise<AxiosResponse<IResponseNumber>> {
    return baseApi.post('numbers', params)
  }

  updateNumberPtf(params: IParamsUpdateNumberPtf): Promise<AxiosResponse<IResponsePtfNumber>> {
    return baseApi.post('numbers/pft', params)
  }

  assignInbox(
    inboxId: number,
    data: IParamsAssignInbox
  ): Promise<AxiosResponse<IResponseNumber[]>> {
    return baseApi.post(`numbers/assign-inbox/${inboxId}`, data)
  }

  createBulkInboxPerNumber(
    data: IParamsCreateBulkInboxPerNumber
  ): Promise<AxiosResponse<IResponseCreateBulkInboxPerNumber>> {
    return baseApi.post('/teams/unassigned/bulk', data)
  }

  releaseNumber(
    numberId: number,
    params: IParamsReleaseNumber
  ): Promise<AxiosResponse<IResponseReleaseNumber>> {
    return baseApi.delete(`numbers/${numberId}/release`, { params })
  }

  deleteNumbersLandLineDelete(
    params: IParamsDeleteNumbersLandLineDelete
  ): Promise<AxiosResponse<IResponseReleaseNumber>> {
    return baseApi.delete('numbers/landline/delete', { params })
  }

  checkAircallNumber(id: string | number): Promise<AxiosResponse<boolean>> {
    return baseApi.post(`aircall/numbers/${id}/check`)
  }

  getNumbersLookup(
    params: IParamsGetNumbersLookup,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetNumbersLookup>> {
    return baseApi.get('numbers/lookup', { ...options, params: params })
  }

  checkNumberOwnership(
    params: IParamsCheckNumberOwnership,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCheckNumberOwnership>> {
    return baseApi.get('numbers/check-ownership', { ...options, params })
  }

  createNumbersUnassigned(
    params: IParamsCreateNumbersUnassigned
  ): Promise<AxiosResponse<IResponseNumber>> {
    return baseApi.post('numbers/unassigned/create', params)
  }

  getNumbersLandline(
    params: IParamsGetNumbersLandline,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseGetNumbersLandline>> {
    return baseApi.get('numbers/landline/fetch', { ...options, params: params })
  }

  createNumbersLandline(
    params: IParamsCreateNumbersLandline,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreateNumbersLandline>> {
    return baseApi.post('numbers/landline/create', params, options)
  }

  createNumbersLandlineVerifyRequest(
    params: IParamsCreateNumbersLandlineVerifyRequest,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreateNumbersLandlineVerifyRequest>> {
    return baseApi.post('numbers/landline/verify/request', params, options)
  }

  createNumbersLandlineDocument(
    params: IParamsCreateNumbersLandlineDocument,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseCreateNumbersLandlineDocument>> {
    return baseApi.post('numbers/landline/document/create', params, options)
  }

  createNumbersLandlineInboxUpdate(
    params: IParamsNumbersLandlineInboxUpdate,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseNumbersLandlineInboxUpdate>> {
    return baseApi.post('numbers/landline/inbox/update', params, options)
  }

  createNumbersLandlineTeamCreate(
    params: IParamsNumbersLandlineTeamCreate,
    options?: AxiosOptions
  ): Promise<AxiosResponse<IResponseNumbersLandlineTeamCreate>> {
    return baseApi.post('numbers/landline/team/create', params, options)
  }

  getIncomingTwilioNumbers(): Promise<AxiosResponse<IResponseIncomingTwilioNumbers>> {
    return baseApi.get('numbers/import/prepare')
  }

  getImportNumbersStatus(): Promise<AxiosResponse<IResponseImportNumbersStatus>> {
    return baseApi.get('numbers/import/status')
  }

  importProcess(): Promise<AxiosResponse> {
    return baseApi.post('numbers/import/process')
  }

  unassignNumberFromInbox(
    numberId: number,
    params: IParamsUnassignNumberFromInbox
  ): Promise<AxiosResponse<IResponseReleaseNumber>> {
    return baseApi.post(`numbers/${numberId}/unassign-from-inbox`, params)
  }

  updateCallForwarding(
    numberId: number,
    params: IParamsUpdateCallForwarding
  ): Promise<AxiosResponse<IResponseCallForwarding>> {
    return baseApi.post(`numbers/${numberId}/call-forwarding`, params)
  }
}
export const PhoneApi = new Api()
